'use client';

import { type ComponentProps } from 'react';
import cn from 'classnames';
import { getUrlString } from 'helpers/helpers';
import { type SanityPromo } from '@metmuseum/types';
import ChevronR from 'assets/icons/chevron-r.svg';
import LinkButton from 'components/controls/links/link-button';
import MarkdownParser from 'components/rich-text/markdown-parser';
import RedundantLink from 'components/accesibility/redundant-link';
import RichText from 'components/rich-text';
import SanityImage from '@sanity-image';
import styles from 'components/promos/promos.module.scss';
import { useTranslations } from 'next-intl';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import hoverStyles from '../../cards/hover-state.module.scss';
import { getActionAttributes } from 'lib/analytics/analyticsAttributes';

export const PromoCard = ({
	promo,
	isReversed,
	hasBorder = false,
	analytics,
}: {
	promo: SanityPromo;
	isReversed?: boolean;
	hasBorder?: boolean;
	analytics?: ComponentProps<typeof LinkButton>['analytics'];
}) => {
	const t = useTranslations();
	const promoLink = getUrlString(promo);
	const analyticsAttributes = getActionAttributes({
		contentId: promo._id,
		contentName: promo.title,
	});
	const linkAnalytics = analytics?.Content_Block
		? {
				Content_Name: promo.title,
				Content_Block: analytics.Content_Block,
				Content_Type: 'promo',
				event: analytics.event || 'select_content',
			}
		: {};
	const {
		ref: imageAnimateRef,
		className: imageAnimateClassName,
		style: imageAnimateStyle,
	} = useAnimateIntoView();
	const {
		ref: copyAnimateRef,
		className: copyAnimateClassName,
		style: copyAnimateStyle,
	} = useAnimateIntoView({ duration: '2s' });

	return (
		<div
			key={promo.title}
			className={cn(
				styles.promo,
				isReversed && styles.reversed,
				hasBorder && styles.hasBorder,
				hoverStyles.card,
			)}
		>
			<div className={styles.imageWrapper}>
				<RedundantLink
					href={promoLink}
					{...linkAnalytics}
					{...analyticsAttributes}
				/>
				<figure
					ref={imageAnimateRef}
					style={imageAnimateStyle}
					className={cn(styles.figure, imageAnimateClassName)}
				>
					{promo.image && (
						<SanityImage
							image={promo.image}
							fill={true}
							sizes={'(max-width: 960px) 90vw, 50vw'}
						/>
					)}
				</figure>
			</div>
			<div className={styles.copyWrapper}>
				<div
					ref={copyAnimateRef}
					style={copyAnimateStyle}
					className={cn(styles.copy, copyAnimateClassName)}
				>
					<h3 className={styles.title}>
						<MarkdownParser>{promo.title}</MarkdownParser>
					</h3>
					<div className={styles.bodyText}>
						<RichText value={promo.description} />
					</div>
					{promoLink && (
						<LinkButton
							href={promoLink}
							analytics={linkAnalytics}
							rightIcon={<ChevronR />}
							text={promo.cta || t('readMore')}
							{...analyticsAttributes}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export type PromoAnalytics = ComponentProps<typeof LinkButton>['analytics'];
